// urns a vague technical failure into a real explanation — 
// "your link expired," "this looks like a CORS/blocked-request 
// problem," etc. — instead of a generic "something went wrong."


export type LoadErrorKind = 'expired-sas' | 'cors' | 'http' | 'network';

export interface LoadError {
  kind: LoadErrorKind;
  message: string;
}

// A 403 from Azure Storage on a GET is, in practice, almost always an
// expired (or not-yet-valid) SAS token — call that out by name instead of
// a generic "request failed". Note retry() alone won't fix this: it re-fetches
// the SAME (expired) URL. The real fix is reopening the file, which triggers
// useDocPreview to request a fresh SAS URL from /adls/sas_url — that's what
// this message tells the user to do, not just "try again".
export function classifyHttpStatus(status: number): LoadError {
  if (status === 403) {
    return {
      kind: 'expired-sas',
      message: 'This file’s link has expired. Close this panel and reopen the file to get a fresh one.',
    };
  }
  return {
    kind: 'http',
    message: `Storage returned ${status} while downloading the file.`,
  };
}

// A cross-origin fetch() that's rejected by CORS surfaces to JS as a bare
// "TypeError: Failed to fetch" — no status code, no distinguishing detail.
// That signature is the best client-side signal available for "the storage
// account has no CORS rule for this origin", so we say that instead of
// leaving it looking like a dead network. Endpoint-agnostic — a CORS/dead-
// network failure looks identical whether it happened on our own API or on
// storage, so this is reused as-is for both.
export function classifyFetchError(err: unknown): LoadError {
  if (err instanceof TypeError) {
    return {
      kind: 'cors',
      message: 'The request was blocked before it reached storage — almost certainly a missing CORS rule on the storage account for this origin, not a network outage. Add a CORS rule allowing GET from this origin.',
    };
  }
  return {
    kind: 'network',
    message: err instanceof Error ? err.message : 'Failed to download the file.',
  };
}

// For failures AFTER the server has already responded, while reading the
// body. A response arrived, so CORS can't be the cause here: browsers report
// a connection that drops mid-transfer as a TypeError too, which
// classifyFetchError above would mislabel as CORS.
export function classifyReadError(err: unknown): LoadError {
  if (err instanceof TypeError) {
    return {
      kind: 'network',
      message: 'The connection was interrupted while receiving the file. Check your connection and try again.',
    };
  }
  return {
    kind: 'network',
    message: err instanceof Error ? err.message : 'Failed to download the file.',
  };
}

// Deliberately separate from classifyHttpStatus above, not reused: that
// one's 403 branch is specifically about an already-issued blob SAS token
// expiring, which isn't a concept that applies here — this is the call
// that ISSUES the SAS in the first place. A 403 here means the backend
// rejected the request itself (no access to this path, bad session, etc.),
// so "reopen the file to get a fresh link" would be actively wrong advice.
export function classifySasEndpointStatus(status: number): LoadError {
  if (status === 403) {
    return {
      kind: 'http',
      message: "You don't have access to this file, or your session may need refreshing.",
    };
  }
  if (status === 404) {
    return {
      kind: 'http',
      message: 'This file could not be found — it may have been moved or deleted.',
    };
  }
  return {
    kind: 'http',
    message: `Failed to get a link to this file (${status}).`,
  };
}
